import type { Bundle } from '@/components/Store.d';
import { config } from '@/lib/config';
import { getBundleUrl } from '@/lib/mapping';
import { isAnnualSubscription } from '@/lib/util';

export type BannerActionType = 'show-upsell' | 'show-not-qualified' | 'none';

export interface BannerDecision {
	action: BannerActionType;
	showSmartPlusPurHint?: boolean;
}

export interface DecideBannerOptions {
	bundle: Bundle | null;
	hasP7Entitlements: boolean;
	isLoggedIn: boolean;
	moveFromBundleRef?: string;
}

/**
 * Determines which banner to show based on product and entitlements
 * Simple logic based on the product matrix:
 * - Has P7 entitlements + viewing PUR/Smart → show cheaper PUR-KOMBI upsell
 * - No P7 entitlements + viewing PUR-KOMBI → show not qualified, redirect to regular PUR
 * - No P7 entitlements + has Smart ohne Pur + viewing PUR → show buy button + Smart+Pur hint
 *
 * @param {DecideBannerOptions} options - Configuration options
 * @returns {BannerDecision} What banner to show
 */
export function decideBanner({
	bundle,
	hasP7Entitlements,
	isLoggedIn,
	moveFromBundleRef
}: DecideBannerOptions): BannerDecision {
	if (!bundle || !isLoggedIn) {
		return { action: 'none' };
	}

	const env = (import.meta.env.VITE_APP_ENVIRONMENT || 'development') as
		| 'development'
		| 'production';
	const refs = config[env].bundleRefs;
	const bundleRef = bundle.bundleReference;
	const smartRef = 'smart' in refs ? refs.smart : undefined;

	// User has print subscription → offer PUR-KOMBI
	if (hasP7Entitlements) {
		const canGetPurKombi =
			bundleRef === refs.pur ||
			bundleRef === refs.purYearly ||
			bundleRef === refs.smartPlusPur ||
			(smartRef !== undefined && bundleRef === smartRef);

		return canGetPurKombi ? { action: 'show-upsell' } : { action: 'none' };
	}

	// User has NO print subscription but is on subscriber-only product → not qualified
	const isOnSubscriberOnlyProduct =
		bundleRef === refs.purForSubscribers || bundleRef === refs.purAndPrintYearly;

	if (isOnSubscriberOnlyProduct) {
		return { action: 'show-not-qualified' };
	}

	// User has Smart (ohne PUR) and is viewing PUR → show buy button with Smart+PUR hint
	// smart is only present in dev until the production bundleRef is added
	if (smartRef && moveFromBundleRef === smartRef) {
		const isViewingPur = bundleRef === refs.pur || bundleRef === refs.purYearly;
		if (isViewingPur) {
			return { action: 'none', showSmartPlusPurHint: true };
		}
	}

	return { action: 'none' };
}

/**
 * Gets the redirect URL for the PUR-KOMBI upselling flow
 * @param {boolean} isAnnual - Whether the current subscription is annual
 * @param {string} [printAccountNumber] - Optional print account number
 * @returns {URL} The complete URL with query parameters
 */
export function getPurKombiUrl(isAnnual: boolean, printAccountNumber?: string): URL {
	const ctaPath = isAnnual
		? '/produkt/der-standard-at-pur-kombi-jahresabo'
		: '/produkt/der-standard-at-pur-kombi-fuer-abonnenten';

	const ctaUrl = new URL(ctaPath, import.meta.env.VITE_APP_CHECKOUT_BASE_URL);

	const params = {
		ref: 'PURShop',
		utm_source: 'derstandard',
		utm_medium: 'aboseite',
		utm_campaign: isAnnual ? 'PUR-Kombi-Jahresabo' : 'PUR-Kombi',
		utm_content: 'Upselling'
	};

	Object.entries(params).forEach(([key, value]) => {
		ctaUrl.searchParams.append(key, value);
	});

	if (printAccountNumber) {
		ctaUrl.searchParams.append('pan', printAccountNumber);
	}

	return ctaUrl;
}

/**
 * Gets the checkout URL for the Smart + PUR product
 * @returns {string} The relative checkout path for the Smart+PUR product
 */
export function getSmartPlusPurUrl(): string {
	return getBundleUrl('smartPlusPur');
}

/**
 * Gets the redirect URL for the "not qualified" flow (redirect to PUR)
 * @param {Bundle | null} currentBundle - The current bundle to determine if it's annual
 * @returns {string} The complete URL with query parameters
 */
export function getPurUrl(currentBundle: Bundle | null): string {
	const isAnnual = currentBundle ? isAnnualSubscription(currentBundle) : false;

	return getBundleUrl(isAnnual ? 'purYearly' : 'pur');
}

/**
 * Checks if the upgrade text should be shown
 * @param {BannerActionType} bannerAction - Current banner action state
 * @param {boolean} showActiveProductWarning - Whether product is already active
 * @returns {boolean}
 */
export function shouldShowUpgradeText(
	bannerAction?: BannerActionType,
	showActiveProductWarning?: boolean,
	showSubscriptionOnHoliday?: boolean
): boolean {
	// Don't show upgrade text if:
	// - User is not qualified (will be redirected to different product)
	// - User is being shown the upsell banner (don't show both banners together)
	// - Product is already active (user already has this product)
	// - Subscription is on holiday (upgrade is blocked)
	return (
		bannerAction !== 'show-not-qualified' &&
		bannerAction !== 'show-upsell' &&
		!showActiveProductWarning &&
		!showSubscriptionOnHoliday
	);
}
